// paintengine.ts ↔ game/formmgr.cpp — the renderer-owned incremental-paint
// engine. FormMgr (a region's layer stack: pen/key event cooking, capture,
// updatemap-driven paint, scroll accumulation + back-buffer scroll-blt) and
// MultiFormMgr (the root compositor the game loop drives — gpmfrmm: child
// region rects, pen2 capture stealing, DrawFrame).
//
// canvas-removal R3 (ii): this engine was extracted out of ui/formmgr.ts when
// the `Form` mechanism (forms.ini-driven canvas windows) was deleted. R1/R2
// orphaned the entire control-tree machinery (LoadForm → Form.Init →
// createControl → control `new`s) — `LoadForm` became test-only — so the dead
// `Form`/`AddForm`/`LoadForm`/`createControl` paths are gone. What remains is
// the proven dirty-rect engine that drives the renderer-owned PaintLayers
// (SimUIForm/InputUIForm/PlaceStructureForm/UnitTitleLayer) registered via
// AddLayer: the paint walk + scroll + the pen-capture cook are UNCHANGED, so
// the playfield render and in-game input are byte-identical.
//
// The C++ globals are the injected FormMgrHost seams (defined below). PaintLayer
// is the EXACT structural subset of the old Form the engine reads off its
// m_apfrm[] entries: a layer registered via AddLayer occupies the single-form
// slot and the engine drives it through these members.

import type { Event } from '../ui/event.ts';
import {
  newEvent,
  kfRedrawDirty,
  penHoverEvent,
  penDownEvent,
  penMoveEvent,
  penUpEvent,
  penHoldEvent,
  penDownEvent2,
  penMoveEvent2,
  penUpEvent2,
  keyDownEvent,
  nullEvent,
} from '../ui/event.ts';
import type { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import { UpdateMap } from '../gfx/updatemap.ts';
import type { CoordMap } from '../gfx/coords.ts';
import type { TBitmap } from '../gfx/tbitmap.ts';
import { Rect } from '../ui/rect.ts';
import {
  kfFrmDoModal,
  kfFrmVisible,
  kfFrmTranslucent,
  kfFrmNoEcom,
  kfFrmDemandPen2,
} from '../ui/uikit.ts';

// ht.h:2609-2611
export const kcFormsMax = 16;
export const kfFrmmNoScroll = 1;

// ht.h:2678
export const kcFormMgrMax = 5;

// HTML/DOM UI shell plan, Phase 3 (forms.ini elimination): the in-game playfield
// (SimUILayer) and console strip (InputUILayer) are NO LONGER forms.ini-loaded
// Forms — they are renderer-owned plain objects. The FormMgr's proven incremental
// dirty-rect engine (UpdateMap merge/scroll-blt + the paint walk + pen capture
// cooking) is NOT forms.ini and is the byte-identity guarantee, so it STAYS.
// PaintLayer is the EXACT structural subset the FormMgr engine reads off its
// m_apfrm[] entries: a layer registered via FormMgr.AddLayer occupies the single-
// form slot and the engine drives it through these members — no control tree, no
// forms.ini geometry, no Form base, no LoadForm.
export interface PaintLayer {
  // identity/geometry the cook + opaque-rect + capture machinery reads
  m_idf: number;
  m_wf: number;
  m_rc: Rect;
  GetId(): number;
  GetFlags(): number;
  // the back-ref the layer reaches its UpdateMap / dib / Scroll / root through
  m_pfrmm: FormMgr | null;
  GetFormMgr(): FormMgr | null;
  // the dirty-rect engine's per-frame hooks (the playfield draw lives here)
  OnUpdateMapInvalidate(pupd: UpdateMap, prcOpaque: Rect): void;
  OnPaintBackground(pbm: CpuDibBitmap, pupd: UpdateMap | null): void;
  OnPaint(pbm: CpuDibBitmap): void;
  OnPaintControls(pbm: CpuDibBitmap, pupd: UpdateMap | null): void;
  ScrollInvalidate(pupd: UpdateMap | null): void;
  OnScroll(dx: number, dy: number): void;
  FrameStart(): void;
  FrameComplete(): void;
  OnDisplayResize(): void;
  InvalidateRect(prc: Rect | null): void;
  Dispose(): void;
  // the event cook/dispatch entry points (hit-test by rect, key focus, dispatch)
  OnHitTest(pevt: Event): boolean;
  OnKeyTest(pevt: Event | null): boolean;
  OnPenEvent(pevt: Event): boolean;
  EventProc(pevt: Event): boolean;
  // the pen-capture release the FormMgr's pen2-steal calls (a no-op on a layer,
  // which has no control capture)
  BreakCapture(): void;
}

/** The structural subset of the (deleted) forms.ini `Form` the dissolved
 *  DoModal pump reads — GameSession/ShellSession keep a modalStack of these and
 *  drive the per-event arms (form.cpp:578-622). No renderer-owned PaintLayer is
 *  ever modal now (only the deleted forms.ini Form set kfFrmDoModal), so the
 *  stack is always empty at runtime; the contract is retained type-clean so the
 *  pump machinery stays byte-identical. */
export interface ModalForm {
  m_idcLast: number;
  m_idcDefault: number;
  GetFlags(): number;
  EndForm(nResult: number): void;
  OnFilterEvent(pevt: Event): boolean;
  WiAutoSetLastControl(idc: number): void;
  OnControlSelected(idc: number): void;
}

/** The C++ globals form.cpp/formmgr.cpp reach, as injected seams (the renderer
 *  wires the real gevm/gpdisp/gsndm/TexAtlasMgr; harnesses use the defaults
 *  from makeFormMgrHost below). */
export interface FormMgrHost {
  /** gcxTile/gcyTile for the FormMgr's UpdateMap construction. */
  coords: CoordMap;
  /** game.cpp:1290-1308 — display-width form-coordinate scale (320+ wide ⇒ 32). */
  gcxFullScreenFormDiv10: number;
  /** gsndm.PlaySfx */
  playSfx(sfx: number): void;
  /** CreateTBitmap (form.cpp:223) — the form/layer bitmap loader. */
  createTBitmap(name: string): TBitmap | null;
  /** gapfnt[ifnt] (game.cpp:28 + :419-431 — game.ini [Fonts] table). */
  getFont(ifnt: number): import('../gfx/font.ts').Font;
  /** gcxyBorder (game.cpp:1310 — PcFromFc(1), 2 at 320+ wide). */
  gcxyBorder: number;
  /** ggame.GetVar (game.cpp:2675) — ExpandVars' {pvar} lookup; '' when unset. */
  getVar(pszName: string): string;
  /** HostSoundServiceProc — audio pump during long paints (noop on the web). */
  hostSoundServiceProc(): void;
  /** gevm.SetRedrawFlags */
  setRedrawFlags(wf: number): void;
  /** gevm.PostEvent */
  postEvent(pevt: Event, fCoalesce: boolean): void;
  /** gpdisp->GetFrontDib()->GetFlags() & kfDibWantScrolls (formmgr.cpp:438/524) */
  frontDibWantsScrolls(): boolean;
  /** pbmFront->Scroll(&rcSrc, dx, dy) (formmgr.cpp:444) */
  frontDibScroll(prcSrc: Rect, dx: number, dy: number): void;
  /** gpdisp->FrameStart (formmgr.cpp:970) */
  displayFrameStart(): void;
  /** gpdisp->FrameComplete(cfrmm, apupd, arc, fAnyScroll) (formmgr.cpp:985) */
  displayFrameComplete(cfrmm: number, apupd: UpdateMap[], arc: Rect[], fAnyScroll: boolean): void;
  /** Form::DoModal's blocking event pump (form.cpp:578-622), dissolved into the
   *  GameSession/ShellSession tick pump: the form goes on the modal stack and
   *  the per-event arms run, the epilogue fires once kfFrmDoModal clears. No
   *  forms.ini Form exists now so this is never invoked at runtime; retained as
   *  a seam so the modal-pump machinery stays. Optional — harnesses omit it. */
  runModalLoop?(pfrm: ModalForm, onLoopExit: () => void): void;
}

const copyEvt = (dst: Event, src: Event): void => {
  dst.idf = src.idf;
  dst.eType = src.eType;
  dst.chr = src.chr;
  dst.x = src.x;
  dst.y = src.y;
  dst.dw = src.dw;
  dst.ff = src.ff;
  dst.ms = src.ms;
};

/** Host seams with harness-grade defaults (the renderer overrides with the real
 *  gevm/gpdisp/gsndm/TexAtlasMgr wiring). */
export function makeFormMgrHost(
  coords: CoordMap,
  overrides: Partial<FormMgrHost> = {},
): FormMgrHost {
  return {
    coords,
    gcxFullScreenFormDiv10: 32, // game.cpp:1307 — 320+ wide display
    playSfx: () => {},
    createTBitmap: () => null,
    getFont: (ifnt: number) => {
      throw new Error(`FormMgrHost.getFont(${ifnt}) — no font table wired (gapfnt)`);
    },
    gcxyBorder: 2, // game.cpp:1310 PcFromFc(1) at 320+ wide
    getVar: () => '',
    hostSoundServiceProc: () => {},
    setRedrawFlags: () => {},
    postEvent: () => {},
    frontDibWantsScrolls: () => false,
    frontDibScroll: () => {},
    displayFrameStart: () => {},
    displayFrameComplete: () => {},
    ...overrides,
  };
}

// game/formmgr.cpp:5-16
export function CreateFormMgr(host: FormMgrHost, pbm: CpuDibBitmap): FormMgr | null {
  const pfrmm = new FormMgr(host);
  if (!pfrmm.Init(pbm, true)) {
    pfrmm.Dispose();
    return null;
  }
  return pfrmm;
}

export class FormMgr {
  readonly host: FormMgrHost;
  // game/formmgr.cpp:18-29 (ctor) — field initializers
  m_wf = 0;
  m_idfCapture = 0;
  m_cCaptureDowns = 0;
  m_cfrm = 0;
  // PaintLayer is the structural subset the engine reads; a renderer-owned
  // in-game layer (AddLayer) satisfies it. The paint/scroll/event walks below
  // are byte-identical to the historical Form path — they touch only PaintLayer
  // members.
  m_apfrm: (PaintLayer | null)[] = new Array(kcFormsMax).fill(null);
  m_pbm: CpuDibBitmap | null = null; // (uninitialized in the C++ ctor)
  m_pupd: UpdateMap | null = null;
  m_fFreeDib = false;
  m_dxScrollAccumulate = 0;
  m_dyScrollAccumulate = 0;
  /** gpmfrmm — the root MultiFormMgr (the C++ global); wired by AddFormMgr,
   *  a MultiFormMgr is its own root. null = standalone (harness) FormMgr. */
  m_pfrmmRoot: MultiFormMgr | null = null;

  constructor(host: FormMgrHost) {
    this.host = host;
  }

  /** gpmfrmm accessor — a layer reaches the root through this. */
  GetRoot(): MultiFormMgr | null {
    return this.m_pfrmmRoot;
  }

  // game/formmgr.cpp:31-41 — ~FormMgr
  Dispose(): void {
    // (if (m_fFreeDib) delete m_pbm — GC'd)
    while (this.m_cfrm !== 0) {
      const pfrm = this.m_apfrm[0]!;
      this.RemoveForm(pfrm);
      pfrm.Dispose();
    }
    this.m_pupd = null;
  }

  // game/formmgr.cpp:43-48
  OnDisplayResize(): void {
    this.Init(this.m_pbm!, this.m_fFreeDib);
    for (let i = 0; i < this.m_cfrm; i++) {
      this.m_apfrm[i]!.OnDisplayResize();
    }
  }

  // game/formmgr.cpp:51-68 — also called for each active FormMgr when the
  // Display is resized
  Init(pbm: CpuDibBitmap, fFreeDib: boolean): boolean {
    this.m_pbm = pbm;
    this.m_fFreeDib = fFreeDib;
    if (!this.m_pupd) this.m_pupd = new UpdateMap(this.host.coords);
    const siz = pbm.GetSize();
    if (!this.m_pupd.Init(siz)) return false;

    // Reset possibly reused update map.
    this.m_pupd.Reset();
    return true;
  }

  // game/formmgr.cpp:99-110
  RemoveForm(pfrm: PaintLayer): void {
    for (let n = 0; n < this.m_cfrm; n++) {
      if (this.m_apfrm[n] === pfrm) {
        if (n < kcFormsMax - 1) {
          // memmove(&m_apfrm[n], &m_apfrm[n + 1], sizeof(Form *) * (kcFormsMax - 1 - n))
          // (slots ≥ m_cfrm are never read; the C++ leaves a stale tail dup)
          for (let j = n; j < kcFormsMax - 1; j++) this.m_apfrm[j] = this.m_apfrm[j + 1];
          this.m_apfrm[kcFormsMax - 1] = null;
        }
        this.m_cfrm--;
        break;
      }
    }
    pfrm.InvalidateRect(null);
  }

  // game/formmgr.cpp:112-121
  EcomSuppressed(): boolean {
    for (let n = 0; n < this.m_cfrm; n++) {
      if (this.m_apfrm[n] !== null) {
        if (this.m_apfrm[n]!.GetFlags() & kfFrmNoEcom) return true;
      }
    }
    return false;
  }

  // game/formmgr.cpp:123-135
  CookKeyEvent(pevt: Event): boolean {
    // Hack for now: assume the topmost form wants key input
    // Maybe this'll be enough focus "knowledge".

    for (let n = this.m_cfrm - 1; n >= 0; n--) {
      if (this.m_apfrm[n]!.OnKeyTest(pevt)) {
        pevt.idf = this.m_apfrm[n]!.GetId();
        return true;
      }
    }
    return false;
  }

  // game/formmgr.cpp:137-148
  FindPen2Form(): PaintLayer | null {
    // Find a form that demands pen2

    for (let i = 0; i < this.m_cfrm; i++) {
      const pfrm = this.m_apfrm[i]!;
      if (pfrm.m_wf & kfFrmDemandPen2) {
        return pfrm;
      }
    }
    return null;
  }

  // game/formmgr.cpp:150-159
  BreakCapture(): void {
    const pfrm = this.GetFormCapture();
    if (pfrm === null) {
      return;
    }
    pfrm.BreakCapture();
    this.m_idfCapture = 0;
    this.m_cCaptureDowns = 0;
  }

  // game/formmgr.cpp:161-206
  CookPenEvent(pevt: Event): boolean {
    // Send the input to the capturing form. Auto-release if pen up,
    // auto-capture if pen down.

    if (this.m_idfCapture !== 0) {
      pevt.idf = this.m_idfCapture;
      if (pevt.eType === penDownEvent || pevt.eType === penDownEvent2) {
        this.m_cCaptureDowns++;
      }
      if (pevt.eType === penUpEvent || pevt.eType === penUpEvent2) {
        this.m_cCaptureDowns--;
      }
      if (this.m_cCaptureDowns <= 0) {
        this.m_cCaptureDowns = 0;
        this.m_idfCapture = 0;
      }
      return true;
    } else {
      // Ask the topmost form first if it wants this pen input.
      // All coords are global.

      let idf = 0;
      for (let n = this.m_cfrm - 1; n >= 0; n--) {
        const pfrm = this.m_apfrm[n]!;
        if (pfrm.OnHitTest(pevt)) {
          idf = pfrm.GetId();
          break;
        }

        // If the top-most form is modal, don't pass the event on
        // to any lower forms.

        if (pfrm.GetFlags() & kfFrmDoModal) {
          pevt.idf = 0;
          return true;
        }
      }
      pevt.idf = idf;
      if (pevt.eType === penDownEvent || pevt.eType === penDownEvent2) {
        this.m_cCaptureDowns = 1;
        this.m_idfCapture = idf;
      }
      return pevt.idf !== 0;
    }
  }

  // game/formmgr.cpp:208-219
  GetFocus(): PaintLayer | null {
    // Hack for now: assume the topmost form wants key input
    // Maybe this'll be enough focus "knowledge".

    for (let n = 0; n < this.m_cfrm; n++) {
      if (this.m_apfrm[n]!.OnKeyTest(null)) {
        return this.m_apfrm[n];
      }
    }
    return null;
  }

  // game/formmgr.cpp:221-266
  CookEvent(pevt: Event): boolean {
    pevt.idf = 0;
    switch (pevt.eType) {
      case penHoverEvent:
      case penUpEvent:
      case penDownEvent:
      case penMoveEvent:
      case penUpEvent2:
      case penDownEvent2:
      case penMoveEvent2:
      case penHoldEvent: {
        // If no capture, process as usual

        if (this.m_idfCapture === 0) {
          return this.CookPenEvent(pevt);
        }

        // Mouse is captured. If captureed but there is a new form on top,
        // gracefully release the capture (can happen if form shows while
        // mouse is captured)

        if (!this.CookPenEvent(pevt)) return false;
        for (let n = this.m_cfrm - 1; n >= 0; n--) {
          const pfrm = this.m_apfrm[n]!;
          if ((pfrm.GetFlags() & (kfFrmDoModal | kfFrmVisible)) === (kfFrmDoModal | kfFrmVisible)) {
            if (pfrm.m_idf !== pevt.idf) {
              pevt.dw = 1;
              pevt.eType = penUpEvent;
              this.m_idfCapture = 0;
              this.m_cCaptureDowns = 0;
            }
          }
          break;
        }
        return true;
      }

      case keyDownEvent:
        return this.CookKeyEvent(pevt);
    }
    return false;
  }

  // game/formmgr.cpp:268-275
  GetFormPtr(idf: number): PaintLayer | null {
    for (let n = this.m_cfrm - 1; n >= 0; n--) {
      if (this.m_apfrm[n]!.GetId() === idf) return this.m_apfrm[n];
    }
    return null;
  }

  // HTML/DOM UI shell plan, Phase 3 (forms.ini elimination): register a
  // renderer-owned in-game PaintLayer (SimUILayer/InputUILayer) WITHOUT forms.ini.
  // This is the non-Form analogue of LoadForm+AddForm: it puts the layer in the
  // single-form slot (the engine drives it through the PaintLayer members, walk
  // UNCHANGED), wires the back-refs the layer reaches its UpdateMap/dib/Scroll/root
  // through, and full-invalidates so the first frame paints — exactly the
  // AddForm(pfrm) bookkeeping (formmgr.cpp:70-97), minus the control tree.
  AddLayer(layer: PaintLayer): void {
    if (this.m_cfrm >= kcFormsMax) return;
    layer.m_pfrmm = this;
    // a region FormMgr is a child of the root MultiFormMgr (set by AddFormMgr) —
    // the layer reaches gpmfrmm via m_pfrmm.GetRoot()
    this.m_apfrm[this.m_cfrm++] = layer;
    layer.InvalidateRect(null);
  }

  // game/formmgr.cpp:290-311
  CalcOpaqueRect(pfrmStop: PaintLayer | null, prcOpaqueStart: Rect | null, prcResult: Rect): void {
    // Start from the top and expand the opaque rect when a portion lies safely
    // inside the new rectangle.

    if (prcOpaqueStart === null) {
      prcResult.SetEmpty();
    } else {
      prcResult.SetRc(prcOpaqueStart);
    }
    for (let n = this.m_cfrm - 1; n >= 0; n--) {
      const pfrm = this.m_apfrm[n]!;
      if (pfrm === pfrmStop) break;
      const wf = pfrm.GetFlags();
      if (wf & kfFrmTranslucent) continue;
      if (!(wf & kfFrmVisible)) continue;
      prcResult.Add(prcResult, pfrm.m_rc);
    }
  }

  // game/formmgr.cpp:313-369
  Paint(fScrolled: boolean, prcOpaqueStart: Rect | null): void {
    // If scrolling, we want to scroll from a valid back buffer.
    // After invalidation has been calculated for this frame, merge in
    // damaged invalid; this'll make the back buffer valid after redraw.

    if (fScrolled) this.m_pupd!.StartMergeDamagedInvalid();

    // Give some time to sound servicing

    this.host.hostSoundServiceProc();

    // Invalidate UpdateMap for this frame

    const rcOpaque = new Rect();
    for (let j = 0; j < this.m_cfrm; j++) {
      if (!(this.m_apfrm[j]!.m_wf & kfFrmVisible)) continue;
      this.CalcOpaqueRect(this.m_apfrm[j], prcOpaqueStart, rcOpaque);
      if (!rcOpaque.RectIn(this.m_apfrm[j]!.m_rc)) {
        this.m_apfrm[j]!.OnUpdateMapInvalidate(this.m_pupd!, rcOpaque);
      }
    }

    // Give some time to sound servicing

    this.host.hostSoundServiceProc();

    // The merge is a two part process

    if (fScrolled) this.m_pupd!.EndMergeDamagedInvalid();

    // If nothing invalid, nothing to paint

    if (!this.m_pupd!.IsInvalid()) return;

    // Now paint against the update map

    for (let j = 0; j < this.m_cfrm; j++) {
      if (!(this.m_apfrm[j]!.m_wf & kfFrmVisible)) continue;
      if (this.m_pupd!.IsRectInvalid(this.m_apfrm[j]!.m_rc)) {
        this.CalcOpaqueRect(this.m_apfrm[j], prcOpaqueStart, rcOpaque);
        if (!rcOpaque.RectIn(this.m_apfrm[j]!.m_rc)) {
          this.m_apfrm[j]!.OnPaintBackground(this.m_pbm!, this.m_pupd!);
          this.host.hostSoundServiceProc();
          this.m_apfrm[j]!.OnPaint(this.m_pbm!);
          this.host.hostSoundServiceProc();
          this.m_apfrm[j]!.OnPaintControls(this.m_pbm!, this.m_pupd!);
          this.host.hostSoundServiceProc();
        }
      }
    }
  }

  // game/formmgr.cpp:371-405
  Scroll(dx: number, dy: number): void {
    // If nothing to scroll, return

    if (dx === 0 && dy === 0) return;

    // Force the forms to invalidate the updatemap appropriately before the map
    // is scrolled

    for (let i = 0; i < this.m_cfrm; i++) this.m_apfrm[i]!.ScrollInvalidate(this.m_pupd!);

    // Now scroll the update map

    const fScrolled = this.m_pupd!.Scroll(dx, dy);

    // Give forms a chance to respond to the scroll

    for (let i = 0; i < this.m_cfrm; i++) this.m_apfrm[i]!.OnScroll(dx, dy);

    // Now invalidate again after the map has been scrolled

    for (let i = 0; i < this.m_cfrm; i++) this.m_apfrm[i]!.ScrollInvalidate(this.m_pupd!);

    // Accumulate

    if (fScrolled) {
      this.m_dxScrollAccumulate += dx;
      this.m_dyScrollAccumulate += dy;
    }
  }

  // game/formmgr.cpp:407-458
  ScrollBits(): boolean {
    if (this.m_dxScrollAccumulate === 0 && this.m_dyScrollAccumulate === 0) return false;
    const sizSrc = this.m_pbm!.GetSize();
    const rcSrc = new Rect();

    rcSrc.left = 0;
    if (this.m_dxScrollAccumulate < 0) {
      rcSrc.left = -this.m_dxScrollAccumulate;
      this.m_dxScrollAccumulate = 0;
    }
    rcSrc.top = 0;
    if (this.m_dyScrollAccumulate < 0) {
      rcSrc.top = -this.m_dyScrollAccumulate;
      this.m_dyScrollAccumulate = 0;
    }
    rcSrc.right = sizSrc.cx;
    if (this.m_dxScrollAccumulate > 0) rcSrc.right -= this.m_dxScrollAccumulate;
    rcSrc.bottom = sizSrc.cy;
    if (this.m_dyScrollAccumulate > 0) rcSrc.bottom -= this.m_dyScrollAccumulate;

    // Hack: if the front dib is a rotated dib, it is a 3 dib architecture and
    // we can scroll the middle (rotated) dib rather than the back dib, giving
    // us a speedup. (formmgr.cpp:436-450 — the non-WIN branch the web build
    // compiles; the front-dib scroll is a display seam)

    if (this.host.frontDibWantsScrolls()) {
      this.host.frontDibScroll(rcSrc, this.m_dxScrollAccumulate, this.m_dyScrollAccumulate);
      this.m_dxScrollAccumulate = 0;
      this.m_dyScrollAccumulate = 0;
      return true;
    }

    // Perform blt of the back buffer. C++ m_pbm->Blt(m_pbm, ...) self-blits
    // through a GPU pass (reads pre-copy pixels); CpuDibBitmap.Scroll snapshots.

    this.m_pbm!.Scroll(rcSrc, this.m_dxScrollAccumulate, this.m_dyScrollAccumulate);
    this.m_dxScrollAccumulate = 0;
    this.m_dyScrollAccumulate = 0;
    return true;
  }

  // game/formmgr.cpp:460-463
  HasCapture(): boolean {
    return this.m_idfCapture !== 0;
  }

  // game/formmgr.cpp:465-476
  GetFormCapture(): PaintLayer | null {
    if (this.m_idfCapture === 0) {
      return null;
    }
    for (let n = this.m_cfrm - 1; n >= 0; n--) {
      if (this.m_apfrm[n]!.m_idf === this.m_idfCapture) {
        return this.m_apfrm[n];
      }
    }
    return null;
  }

  // game/formmgr.cpp:478-485 — a modal layer (kfFrmDoModal). No renderer-owned
  // PaintLayer ever sets kfFrmDoModal (only the deleted forms.ini Form did), so
  // at runtime this always returns null; the cook still honours kfFrmDoModal in
  // CookPenEvent/CookEvent should a layer ever set it.
  GetModalForm(): PaintLayer | null {
    for (let n = this.m_cfrm - 1; n >= 0; n--) {
      if (
        (this.m_apfrm[n]!.m_wf & (kfFrmDoModal | kfFrmVisible)) ===
        (kfFrmDoModal | kfFrmVisible)
      ) {
        return this.m_apfrm[n];
      }
    }
    return null;
  }

  // game/formmgr.cpp:487-499
  InvalidateRect(prc: Rect | null): void {
    if (prc === null) {
      const siz = this.m_pbm!.GetSize();
      const rcDib = new Rect();
      rcDib.Set(0, 0, siz.cx, siz.cy);
      prc = rcDib;
    }

    if (this.m_pupd !== null) this.m_pupd.InvalidateRect(prc);
  }

  // game/formmgr.cpp:501-504
  GetDib(): CpuDibBitmap {
    return this.m_pbm!;
  }

  // game/formmgr.cpp:506-511
  SetDib(pbm: CpuDibBitmap): CpuDibBitmap {
    const pbmOld = this.m_pbm!;
    this.m_pbm = pbm;
    return pbmOld;
  }

  // game/formmgr.cpp:513-539
  BltTo(pbmDst: CpuDibBitmap, yTop: number, fScrolled: boolean): boolean {
    // If nothing invalid, nothing to do

    if (!this.m_pupd!.IsInvalid()) return false;

    // If we've scrolled, we've already assured the back dib is valid, so just
    // copy it Unless the front dib is handling scrolls in which case do the
    // normal blt tiles thing.

    if (!this.host.frontDibWantsScrolls()) {
      if (fScrolled && !(this.m_wf & kfFrmmNoScroll)) {
        const sizSrc = this.m_pbm!.GetSize();
        const rcSrc = new Rect();
        rcSrc.Set(0, 0, sizSrc.cx, sizSrc.cy);
        pbmDst.Blt(this.m_pbm!, rcSrc, 0, yTop);
        return true;
      }
    }

    // Copy to pbmDst only tiles that are invalid

    pbmDst.BltTiles(this.m_pbm!, this.m_pupd!, yTop);
    return true;
  }

  // game/formmgr.cpp:541-546
  FrameStart(): void {
    for (let n = 0; n < this.m_cfrm; n++) {
      this.m_apfrm[n]!.FrameStart();
    }
  }

  // game/formmgr.cpp:548-553
  FrameComplete(): void {
    for (let n = 0; n < this.m_cfrm; n++) {
      this.m_apfrm[n]!.FrameComplete();
    }
  }

  // ht.h:2642-2650
  SetFlags(wf: number): void {
    this.m_wf = wf;
  }

  GetFlags(): number {
    return this.m_wf;
  }

  GetFormCount(): number {
    return this.m_cfrm;
  }

  // ht.h:2671-2673
  GetUpdateMap(): UpdateMap | null {
    return this.m_pupd;
  }
}

//
// MultiFormMgr
//

export class MultiFormMgr extends FormMgr {
  // game/formmgr.cpp:559-564 (ctor) — field initializers
  m_cfrmm = 0;
  m_apfrmm: (FormMgr | null)[] = new Array(kcFormMgrMax).fill(null);
  m_arcFormMgr: Rect[] = Array.from({ length: kcFormMgrMax }, () => new Rect());
  m_evtPen1Down: Event = newEvent();

  constructor(host: FormMgrHost) {
    super(host);
    this.m_pfrmmRoot = this; // gpmfrmm — a MultiFormMgr is its own root
  }

  // game/formmgr.cpp:566-573 — ~MultiFormMgr
  override Dispose(): void {
    while (this.m_cfrmm !== 0) {
      const pfrmm = this.m_apfrmm[0]!;
      this.RemoveFormMgr(pfrmm);
      pfrmm.Dispose();
    }
    super.Dispose();
  }

  // game/formmgr.cpp:575-580
  override OnDisplayResize(): void {
    super.OnDisplayResize();
    for (let i = 0; i < this.m_cfrmm; i++) this.m_apfrmm[i]!.OnDisplayResize();
  }

  // game/formmgr.cpp:582-588 — also wires the child's root pointer (the C++
  // children reach gpmfrmm globally)
  AddFormMgr(pfrmm: FormMgr, prc: Rect): void {
    // Assert(m_cfrmm < kcFormMgrMax)
    this.m_apfrmm[this.m_cfrmm] = pfrmm;
    this.m_arcFormMgr[this.m_cfrmm].SetRc(prc);
    this.m_cfrmm++;
    pfrmm.m_pfrmmRoot = this;
  }

  // game/formmgr.cpp:590-602
  RemoveFormMgr(pfrmm: FormMgr): void {
    for (let n = 0; n < this.m_cfrmm; n++) {
      if (this.m_apfrmm[n] === pfrmm) {
        if (n < kcFormMgrMax - 1) {
          // memmove over both parallel arrays
          for (let j = n; j < kcFormMgrMax - 1; j++) {
            this.m_apfrmm[j] = this.m_apfrmm[j + 1];
            this.m_arcFormMgr[j].SetRc(this.m_arcFormMgr[j + 1]);
          }
          this.m_apfrmm[kcFormMgrMax - 1] = null;
        }
        this.m_cfrmm--;
        break;
      }
    }
  }

  // game/formmgr.cpp:610-614 — RemoveForm on the root re-invalidates the whole
  // MultiFormMgr (a UnitTitleLayer is AddLayer'd directly onto mfrmm and removed
  // via m_pfrmm.RemoveForm; this preserves that byte-identical invalidation).
  override RemoveForm(pfrm: PaintLayer): void {
    super.RemoveForm(pfrm);
    this.InvalidateRect(null);
  }

  // game/formmgr.cpp:616-619 — QUIRK: children are not consulted
  override EcomSuppressed(): boolean {
    return super.EcomSuppressed();
  }

  // game/formmgr.cpp:621-629 — QUIRK: a non-null own-form focus falls through
  // to `return NULL` (only the child lookup's result is ever returned)
  override GetFocus(): PaintLayer | null {
    const pfrm = super.GetFocus();
    if (pfrm === null) {
      if (this.m_cfrmm > 0) return this.m_apfrmm[this.m_cfrmm - 1]!.GetFocus();
    }
    return null;
  }

  // game/formmgr.cpp:631-733
  StealCapturePen2(pevt: Event, pfrmmChildCapture: FormMgr | null): boolean {
    // If this is penDownEvent, remember the pre-cooked event for later
    // use.

    if (pevt.eType === penDownEvent) {
      copyEvt(this.m_evtPen1Down, pevt);
      return false;
    }

    // The playfield allows multi-touch select. Sometimes
    // finger 1 goes down on a control surrounding the playfield accidentially,
    // and finger 2 goes down on the playfield. In this case, finger 1
    // needs to be "uncaptured", and both finger 1 and finger 2 down events
    // routed to the playfield.

    if (pevt.eType !== penDownEvent2) {
      return false;
    }

    // Find a form that demands pen 2

    let pfrmWantsPen2 = this.FindPen2Form();
    if (pfrmWantsPen2 === null) {
      for (let i = 0; i < this.m_cfrmm; i++) {
        pfrmWantsPen2 = this.m_apfrmm[i]!.FindPen2Form();
        if (pfrmWantsPen2 !== null) {
          break;
        }
      }
    }
    if (pfrmWantsPen2 === null) {
      return false;
    }

    // If this form already has capture, then no need to "steal" capture;
    // process normally.

    const pfrmmWantsPen2 = pfrmWantsPen2.GetFormMgr()!;
    if (pfrmmWantsPen2.HasCapture()) {
      return false;
    }

    // Any modal forms up? Don't steal pen2

    if (this.GetModalForm() !== null) {
      return false;
    }
    for (let i = 0; i < this.m_cfrmm; i++) {
      if (this.m_apfrmm[i]!.GetModalForm() !== null) {
        return false;
      }
    }

    // Pen1 is captured in a different form manager. Steal that, then
    // send pen1down and pen2down to the new form.

    if (pfrmmChildCapture !== null) {
      pfrmmChildCapture.BreakCapture();
    } else {
      if (this.HasCapture()) {
        this.BreakCapture();
      }
    }

    // Force capture to this form specifically. This will by-pass
    // hit testing.

    pfrmmWantsPen2.m_idfCapture = pfrmWantsPen2.GetId();

    // Find the formmgr's rect
    const rc = new Rect();
    rc.SetEmpty();
    for (let i = 0; i < this.m_cfrmm; i++) {
      if (this.m_apfrmm[i] === pfrmmWantsPen2) {
        rc.SetRc(this.m_arcFormMgr[i]);
        break;
      }
    }

    // These events need to be processed in the right order.
    // To do this, they will get posted after being cooked.

    let evtT = newEvent();
    copyEvt(evtT, this.m_evtPen1Down);
    evtT.x -= rc.left;
    evtT.y -= rc.top;
    pfrmmWantsPen2.CookEvent(evtT);
    this.host.postEvent(evtT, false);

    evtT = newEvent();
    copyEvt(evtT, pevt);
    evtT.x -= rc.left;
    evtT.y -= rc.top;
    pfrmmWantsPen2.CookEvent(evtT);
    this.host.postEvent(evtT, false);

    // This current event is going to be processed so change it
    // to something nop-ish.

    pevt.eType = nullEvent;
    pevt.idf = 0;
    return true;
  }

  // game/formmgr.cpp:735-835
  override CookEvent(pevt: Event): boolean {
    // If no child has capture, give the multi form manager a crack at it

    let pfrmmChildCapture: FormMgr | null = null;
    let n: number;
    for (n = 0; n < this.m_cfrmm; n++) {
      if (this.m_apfrmm[n]!.HasCapture()) {
        pfrmmChildCapture = this.m_apfrmm[n];
        break;
      }
    }

    // Treat Pen2 specially.

    if (this.StealCapturePen2(pevt, pfrmmChildCapture)) {
      return false;
    }

    // If no child has it captured and if the multiformmgr wants it, give it

    if (pfrmmChildCapture === null) {
      if (super.CookEvent(pevt)) return true;
    } else {
      // Child has it captured. If the multiformmgr has a modal form, force
      // the child to release capture. Can happen if a form shows while
      // a child has the capture.

      if (this.GetModalForm() !== null) {
        pevt.dw = 1;
        pevt.eType = penUpEvent;

        // Set this to zero so it doesn't get out of whack when two
        // fingers are down. It won't underflow since that is checked.

        pfrmmChildCapture.m_cCaptureDowns = 0;
      }
    }

    // Try to handle intelligently

    switch (pevt.eType) {
      case penHoverEvent:
      case penDownEvent:
      case penUpEvent:
      case penMoveEvent:
      case penDownEvent2:
      case penUpEvent2:
      case penMoveEvent2:
      case penHoldEvent:
        // Send to the form with the capture

        for (n = 0; n < this.m_cfrmm; n++) {
          if (this.m_apfrmm[n]!.HasCapture() || this.m_apfrmm[n]!.GetModalForm() !== null) {
            pevt.x -= this.m_arcFormMgr[n].left;
            pevt.y -= this.m_arcFormMgr[n].top;
            if (this.m_apfrmm[n]!.CookEvent(pevt)) return true;

            // else allow it to choose not to handle an event even if it
            // had capture IE when it releases capture/ends modal and wants
            // to pass on that event.

            // restore the pevt
            pevt.x += this.m_arcFormMgr[n].left;
            pevt.y += this.m_arcFormMgr[n].top;
          }
        }

        // Send to the form the event is inside

        for (n = 0; n < this.m_cfrmm; n++) {
          if (this.m_arcFormMgr[n].PtIn(pevt.x, pevt.y)) {
            pevt.x -= this.m_arcFormMgr[n].left;
            pevt.y -= this.m_arcFormMgr[n].top;
            return this.m_apfrmm[n]!.CookEvent(pevt);
          }
        }
        break;

      case keyDownEvent:
        if (this.CookKeyEvent(pevt)) return true;

      // fall through

      default:
        // Send to the formmgr added last

        if (this.m_cfrmm > 0) {
          return this.m_apfrmm[this.m_cfrmm - 1]!.CookEvent(pevt);
        }
    }

    return false;
  }

  // game/formmgr.cpp:837-850
  override GetFormPtr(idf: number): PaintLayer | null {
    let pfrm = super.GetFormPtr(idf);
    if (pfrm !== null) return pfrm;

    for (let n = 0; n < this.m_cfrmm; n++) {
      pfrm = this.m_apfrmm[n]!.GetFormPtr(idf);
      if (pfrm !== null) return pfrm;
    }

    return null;
  }

  // game/formmgr.cpp:852-866
  override CalcOpaqueRect(
    pfrmStop: PaintLayer | null,
    prcOpaqueStart: Rect | null,
    prcResult: Rect,
  ): void {
    // If this form is part of the multi form mgr, then it's simple

    if (pfrmStop === null || pfrmStop.GetFormMgr() === this) {
      super.CalcOpaqueRect(pfrmStop, prcOpaqueStart, prcResult);
      return;
    }

    // Otherwise it's whatever is in the multi form mgr plus the child that
    // this form is within

    super.CalcOpaqueRect(null, prcOpaqueStart, prcResult);
    pfrmStop.GetFormMgr()!.CalcOpaqueRect(pfrmStop, prcResult, prcResult);
  }

  // game/formmgr.cpp:868-889
  GetFormMgrRect(pfrmm: FormMgr, prc: Rect): boolean {
    if (pfrmm === this) {
      const sizDib = this.m_pbm!.GetSize();
      prc.left = 0;
      prc.top = 0;
      prc.right = sizDib.cx;
      prc.bottom = sizDib.cy;
      return true;
    }

    for (let n = 0; n < this.m_cfrmm; n++) {
      if (this.m_apfrmm[n] === pfrmm) {
        prc.SetRc(this.m_arcFormMgr[n]);
        return true;
      }
    }

    // Assert() dropped
    return false;
  }

  // game/formmgr.cpp:891-899
  SetFormMgrRect(pfrmm: FormMgr, prc: Rect): void {
    for (let n = 0; n < this.m_cfrmm; n++) {
      if (this.m_apfrmm[n] === pfrmm) {
        this.m_arcFormMgr[n].SetRc(prc);
        return;
      }
    }
  }

  // game/formmgr.cpp:901-949
  override Paint(fForceBackBufferValid: boolean): boolean {
    // Calc opaque rect

    const rcOpaque = new Rect();
    rcOpaque.SetEmpty();
    this.CalcOpaqueRect(null, null, rcOpaque);

    let fAnyScroll = false;
    let n: number;
    for (n = 0; n < this.m_cfrmm; n++) {
      // Paint children form mgr bits

      let fScrolled = this.m_apfrmm[n]!.ScrollBits();
      if (fForceBackBufferValid) fScrolled = true;
      if (fScrolled) fAnyScroll = true;

      // Paint / calc updatemaps for children

      const rcT = new Rect();
      rcT.Intersect(this.m_arcFormMgr[n], rcOpaque);
      rcT.Offset(-this.m_arcFormMgr[n].left, -this.m_arcFormMgr[n].top);
      this.m_apfrmm[n]!.Paint(fScrolled, rcT);
    }

    // There is a form, need the children updatemap data to be reflected in the
    // parent updatemap

    for (n = 0; n < this.m_cfrmm; n++) {
      const pfrmm = this.m_apfrmm[n]!;
      const pupd = pfrmm.GetUpdateMap()!;
      let fFirst = true;
      for (;;) {
        const rcEnum = pupd.EnumUpdateRects(fFirst, null);
        if (rcEnum === null) break;
        fFirst = false;
        const rcT = new Rect();
        rcT.SetRc(rcEnum);
        rcT.Offset(this.m_arcFormMgr[n].left, this.m_arcFormMgr[n].top);
        this.m_pupd!.InvalidateRect(rcT);
      }
    }

    // Now that the multi-form mgr has all the invalid state reflected,
    // it is ok to paint

    rcOpaque.SetEmpty();
    super.Paint(false, rcOpaque);
    return fAnyScroll;
  }

  // game/formmgr.cpp:951-959
  override BltTo(pbmDst: CpuDibBitmap, yTop: number, fScrolled: boolean): boolean {
    let fBlt = false;
    for (let n = 0; n < this.m_cfrmm; n++) {
      fBlt = this.m_apfrmm[n]!.BltTo(pbmDst, this.m_arcFormMgr[n].top, fScrolled) || fBlt;
    }

    return fBlt;
  }

  // game/formmgr.cpp:961-1015
  DrawFrame(fForceBackBufferValid: boolean, fPaint = true): void {
    // Tell forms about to draw frame

    for (let n = 0; n < this.m_cfrmm; n++) this.m_apfrmm[n]!.FrameStart();

    // Notify display we're starting a frame

    this.host.displayFrameStart();

    // Draw forms

    const fAnyScroll = fPaint ? this.Paint(fForceBackBufferValid) : false;

    // Notify the display we've completed a frame. Pass in update maps
    // so selective invalidation can occur

    const apupd: UpdateMap[] = [];
    const arc: Rect[] = [];
    for (let n = 0; n < this.m_cfrmm; n++) {
      apupd[n] = this.m_apfrmm[n]!.GetUpdateMap()!;
      arc[n] = this.m_arcFormMgr[n];
    }
    this.host.displayFrameComplete(this.m_cfrmm, apupd, arc, fAnyScroll);

    // (avi frame capture is WIN-only; DRAW_UPDATERECTS is debug-only — skipped)

    // Validate updatemaps

    this.m_pupd!.Validate();
    for (let n = 0; n < this.m_cfrmm; n++) {
      this.m_apfrmm[n]!.GetUpdateMap()!.Validate();
    }

    // Tell forms the frame is complete.

    for (let n = 0; n < this.m_cfrmm; n++) {
      this.m_apfrmm[n]!.FrameComplete();
    }
  }

  // game/formmgr.cpp:1036-1057 — QUIRK: the multi's own m_pupd is NOT touched;
  // own-form damage reaches it only via the child-updatemap reflection in Paint
  override InvalidateRect(prc: Rect | null): void {
    // Distribute to this form mgr

    if (prc === null) {
      const siz = this.m_pbm!.GetSize();
      const rcDib = new Rect();
      rcDib.Set(0, 0, siz.cx, siz.cy);
      prc = rcDib;
    }

    // Distribute to children form mgr's

    for (let n = 0; n < this.m_cfrmm; n++) {
      const rcT = new Rect();
      if (rcT.Intersect(prc, this.m_arcFormMgr[n])) {
        rcT.Offset(-this.m_arcFormMgr[n].left, -this.m_arcFormMgr[n].top);
        this.m_apfrmm[n]!.InvalidateRect(rcT);
      }
    }
  }

  // game/formmgr.cpp:1059-1067
  CheckSetRedrawDirty(): void {
    for (let n = 0; n < this.m_cfrmm; n++) {
      if (this.m_apfrmm[n]!.GetUpdateMap()!.IsInvalid()) {
        this.host.setRedrawFlags(kfRedrawDirty); // gevm.SetRedrawFlags(kfRedrawDirty)
        return;
      }
    }
  }

  // game/formmgr.cpp:1069-1077
  IsInvalid(): boolean {
    for (let n = 0; n < this.m_cfrmm; n++) {
      if (this.m_apfrmm[n]!.GetUpdateMap()!.IsInvalid()) {
        return true;
      }
    }
    return false;
  }

  // (STATS_DISPLAY GetUpdateRectCount formmgr.cpp:1079-1096 — debug-only, skipped)
}
